import { MouseEventHandler, useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Box, FormControlLabel, Link, Typography } from "@mui/material";

import { LegalDocumentLink } from "@covertree/ui/link";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { StyledCheckbox } from "@covertree/ui";
import { createDownloadableLink, Toast } from "@covertree/utils";

import { ConsentsFormState } from "../../types";
import { useCheckout } from "../../../CheckoutProvider";

export function AgentConsents() {
  const {
    anythingLoading,
    documents,
    getQuoteDocuments,
    forceAgentSignResponsibility,
  } = useCheckout();
  const policy = useNewBusinessSelector(selectPolicy);

  const showSignResponsibilityConsent = useMemo(
    () => !policy?.isInternalAgency || forceAgentSignResponsibility,
    [policy?.isInternalAgency, forceAgentSignResponsibility]
  );

  const onPolicyApplicationDownload: MouseEventHandler<HTMLAnchorElement> =
    useCallback(
      async (event) => {
        event.preventDefault();

        if (anythingLoading) {
          return;
        }

        if (!documents) {
          Toast.showInfo(
            "Preparing Policy Application. It will automatically download when ready."
          );
          const fetchedDocuments = await getQuoteDocuments();
          if (fetchedDocuments) {
            createDownloadableLink(fetchedDocuments.applicationDocumentUrl);
          }
        } else {
          createDownloadableLink(documents.applicationDocumentUrl);
        }
      },
      [documents, getQuoteDocuments, anythingLoading]
    );

  const { control } = useFormContext<ConsentsFormState>();

  return (
    <>
      <Typography>Purchase Agreement</Typography>
      <Typography variant="subtitle2" my={2} fontWeight={400}>
        By clicking the &#34;Purchase Policy&#34; button below, you are
        indicating that you have reviewed the{" "}
        <Link
          href="#"
          color="secondary"
          underline="hover"
          onClick={onPolicyApplicationDownload}>
          Policy Application
        </Link>
        , <LegalDocumentLink document="PrivacyNotice" underline="hover" />,{" "}
        <LegalDocumentLink document="ElectronicDelivery" underline="hover" />,
        and{" "}
        <LegalDocumentLink
          document="RecurringPaymentNotice"
          underline="hover"
        />{" "}
        with the applicant and have consent from the applicant to proceed.
      </Typography>
      {showSignResponsibilityConsent && (
        <Controller
          render={({ field }) => (
            <Box
              sx={(theme) => ({
                backgroundColor: theme.palette.action.selected,
                borderRadius: theme.spacing(1.5),
                p: 2,
              })}>
              <Typography variant="subtitle2" fontWeight={400}>
                By clicking the checkbox, you further agree that industry best
                practices were followed in the solicitation of this insurance
                and will retain all policyholder records such as
                signed/initialed applications and selection/rejection forms,
                including corresponding metadata/recordings associated with
                electronic signatures obtained. It means that Policy Application
                Esign request will not be sent to the consumer.
              </Typography>
              <FormControlLabel
                sx={{
                  alignItems: "center",
                  mx: 0,
                  mt: 2,
                }}
                disabled={anythingLoading || !!forceAgentSignResponsibility}
                control={
                  <StyledCheckbox
                    squareIcon={true}
                    id="agents-sign-responsibility"
                    data-testid="agents-sign-responsibility"
                    checked={!!forceAgentSignResponsibility || !!field.value}
                    onChange={
                      !forceAgentSignResponsibility ? field.onChange : undefined
                    }
                    disabled={anythingLoading || !!forceAgentSignResponsibility}
                  />
                }
                label={
                  <Typography ml={1} variant="subtitle2" fontWeight={400}>
                    I agree to the above statement
                  </Typography>
                }
              />
            </Box>
          )}
          name="agentSignResponsibility"
          control={control}
        />
      )}
    </>
  );
}
